import type { ChecklistItemPublic, LifecycleChecklistPublic } from '@accounts/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { listItEmailMemberships } from '../../../../lib/api';
import { draftChecklistItEmail } from '../../../../lib/lifecycleApi';
import { queryKeys } from '../../../../lib/queryKeys';

import { checklistItPlan } from './checklist';
import { renderChecklistItEmail } from './checklistText';
import { itStanding } from './picker';

/**
 * What the dialog opens on. `items` are fixed when the draft is made: they are the records
 * the email will track, and `text` names exactly them (or, with none, it is Copy only).
 */
export interface ChecklistItEmailDraft {
  checklist: LifecycleChecklistPublic;
  items: ChecklistItemPublic[];
  text: string;
}

/**
 * DRAFT IT EMAIL for one onboarding or offboarding checklist (spec row 7; FINAL demo: the
 * checklist card's button, and the email offered right after Offboard). The membership read
 * comes first, so the draft knows which of the checklist's records are already with IT and
 * the text never names a record the email does not track (`checklistItPlan`). The server
 * writes the text (and audits the draft, or answers 409); where only some of the records can
 * move, the same lifecycle text is rendered over just those (`renderChecklistItEmail`), so
 * each line keeps its lifecycle action. Callers render `ChecklistItEmailDialog` while `open`
 * is set and handle a refusal themselves (409: nothing on the checklist for IT).
 */
export function useChecklistItEmail() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<ChecklistItEmailDraft | null>(null);
  const draft = useMutation({
    mutationFn: async (checklist: LifecycleChecklistPublic): Promise<ChecklistItEmailDraft> => {
      const memberships = await queryClient.fetchQuery({
        queryKey: queryKeys.itEmailMemberships,
        queryFn: listItEmailMemberships,
      });
      const plan = checklistItPlan(checklist, itStanding(memberships.data));
      const serverText = await draftChecklistItEmail(checklist.id);
      const text =
        plan.kind === 'records' ? renderChecklistItEmail(checklist, plan.items) : serverText;
      return { checklist, items: plan.items, text };
    },
    onSuccess: setOpen,
  });
  return {
    open,
    close: () => {
      setOpen(null);
    },
    draft,
  };
}
